// PDF executivo da Obra/Contrato: dados, parcelas, material consumido e resultado.
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { pdfKit } from "@/lib/financialReportPDF";

const {
  NAVY, TEXT, MUTED, GREEN, RED, AMBER, GOLD,
  PAGE_W, M, fmtBRL, fmtDate, drawHeader, drawCards, drawFooters, TABLE_STYLES,
} = pdfKit;

const STATUS_LANC = { pago: ["Pago", GREEN], pendente: ["Pendente", AMBER], cancelado: ["Cancelado", MUTED] };
const STATUS_CONTRATO = { rascunho: "Rascunho", ativo: "Ativo", concluido: "Concluído", cancelado: "Cancelado" };
const safe = (s) => String(s || "").replace(/[^a-zA-Z0-9-_]+/g, "_").slice(0, 40);

export async function gerarRelatorioObraPDF({ contrato, custoMateriais }) {
  const dataEmissao = new Date().toLocaleString("pt-BR");
  const lancs = (contrato.lancamentos || []).filter((l) => l.status !== "cancelado");
  const pago = lancs.filter((l) => l.status === "pago").reduce((s, l) => s + Number(l.valor || 0), 0);
  const pendente = lancs.filter((l) => l.status === "pendente").reduce((s, l) => s + Number(l.valor || 0), 0);
  const material = Number(custoMateriais?.total || 0);
  const resultado = pago - material;

  const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "landscape", compress: true });
  let y = drawHeader(doc, {
    docLabel: "Relatório da Obra",
    tituloParts: [
      { text: `#${contrato.numero || "—"}`, color: GOLD },
      { text: contrato.titulo || "Contrato", color: NAVY },
    ],
    subtitle: "Resumo executivo da obra: cronograma financeiro, material do estoque consumido e resultado parcial.",
    filtros: [
      { label: "Cliente", value: contrato.cliente?.nome || contrato.cliente_nome || "—" },
      { label: "Vigência", value: `${fmtDate(contrato.data_inicio)} a ${fmtDate(contrato.data_fim)}` },
      { label: "Status", value: STATUS_CONTRATO[contrato.status] || contrato.status || "—" },
    ],
    dataEmissao,
  });

  y = drawCards(doc, y, [
    { label: "Valor do Contrato", value: fmtBRL(contrato.valor_total) },
    { label: "Recebido", value: fmtBRL(pago), bg: [232, 245, 233], border: [165, 214, 167], color: GREEN },
    { label: "Pendente", value: fmtBRL(pendente), bg: [255, 243, 224], border: [255, 204, 128], color: AMBER },
    { label: "Material do Estoque", value: fmtBRL(material), bg: [255, 235, 238], border: [239, 154, 154], color: RED },
    {
      label: "Resultado Parcial",
      value: `${resultado < 0 ? "-" : ""}${fmtBRL(Math.abs(resultado))}`,
      bg: resultado >= 0 ? [232, 245, 233] : [255, 235, 238],
      border: resultado >= 0 ? [165, 214, 167] : [239, 154, 154],
      color: resultado >= 0 ? GREEN : RED,
    },
  ]);

  let nextY = y;
  const sectionTitle = (txt) => {
    if (nextY > 185) { doc.addPage(); nextY = 16; }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9.5);
    doc.setTextColor(...NAVY);
    doc.text(txt, M, nextY);
    doc.setDrawColor(...GOLD);
    doc.setLineWidth(0.6);
    doc.line(M, nextY + 1.2, M + doc.getTextWidth(txt), nextY + 1.2);
    nextY += 4;
  };

  // ---- Parcelas ----
  sectionTitle(`Cronograma Financeiro (${lancs.length} parcela${lancs.length === 1 ? "" : "s"})`);
  if (lancs.length === 0) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...MUTED);
    doc.text("Nenhuma parcela cadastrada.", M, nextY + 4);
    nextY += 10;
  } else {
    const ordenados = [...lancs].sort((a, b) => (a.data_vencimento || "9999").localeCompare(b.data_vencimento || "9999"));
    autoTable(doc, {
      ...TABLE_STYLES,
      startY: nextY,
      head: [["Parcela", "Descrição", "Vencimento", "Valor", "Status", "Pagamento", "Banco"]],
      body: ordenados.map((l) => [
        l.parcela || "—", l.descricao || "—", fmtDate(l.data_vencimento), fmtBRL(l.valor),
        (STATUS_LANC[l.status] || [l.status])[0],
        l.data_pagamento ? fmtDate(l.data_pagamento) : "—", l.banco_nome || "—",
      ]),
      foot: [[
        { content: `TOTAL   (recebido: ${fmtBRL(pago)} • pendente: ${fmtBRL(pendente)})`, colSpan: 3, styles: { halign: "right" } },
        { content: fmtBRL(pago + pendente), styles: { halign: "right" } },
        "", "", "",
      ]],
      columnStyles: {
        0: { cellWidth: 18, halign: "center" },
        1: { cellWidth: 110 },
        2: { cellWidth: 23, halign: "center" },
        3: { cellWidth: 26, halign: "right", fontStyle: "bold" },
        4: { cellWidth: 20, halign: "center", fontStyle: "bold" },
        5: { cellWidth: 23, halign: "center" },
        6: { cellWidth: 36 },
      },
      didParseCell: (data) => {
        if (data.section === "body" && data.column.index === 4) {
          const entry = Object.values(STATUS_LANC).find(([lab]) => lab === data.cell.raw);
          if (entry) data.cell.styles.textColor = entry[1];
        }
      },
    });
    nextY = (doc.lastAutoTable ? doc.lastAutoTable.finalY : nextY) + 8;
  }

  // ---- Material consumido ----
  const materiais = custoMateriais?.materiais || [];
  sectionTitle(`Material do Estoque Consumido (${custoMateriais?.movimentacoes || 0} saída${(custoMateriais?.movimentacoes || 0) === 1 ? "" : "s"})`);
  if (materiais.length === 0) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...MUTED);
    doc.text("Nenhuma saída de material registrada para esta obra.", M, nextY + 4);
    nextY += 10;
  } else {
    autoTable(doc, {
      ...TABLE_STYLES,
      startY: nextY,
      head: [["Código", "Material", "Quantidade", "Valor Total", "% do Material"]],
      body: materiais.map((m) => [
        m.codigo || "—", m.nome || "—",
        Number(m.quantidade).toLocaleString("pt-BR"), fmtBRL(m.valor),
        material > 0 ? `${((m.valor / material) * 100).toFixed(1)}%` : "—",
      ]),
      foot: [[
        { content: "TOTAL", colSpan: 3, styles: { halign: "right" } },
        { content: fmtBRL(material), styles: { halign: "right" } },
        "100%",
      ]],
      columnStyles: {
        0: { cellWidth: 22, halign: "center" },
        1: { cellWidth: 140 },
        2: { cellWidth: 30, halign: "right" },
        3: { cellWidth: 32, halign: "right", fontStyle: "bold" },
        4: { cellWidth: 25, halign: "right" },
      },
    });
    nextY = (doc.lastAutoTable ? doc.lastAutoTable.finalY : nextY) + 8;
    if (custoMateriais?.ultima_saida) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.5);
      doc.setTextColor(...MUTED);
      doc.text(`Última saída de material em ${custoMateriais.ultima_saida.split("-").reverse().join("/")}.`, M, nextY - 3);
    }
  }

  drawFooters(doc, `Contrato #${contrato.numero || "—"} • ${contrato.cliente?.nome || contrato.cliente_nome || ""}`, dataEmissao);
  return {
    blob: doc.output("blob"),
    filename: `Relatorio_Obra_${contrato.numero || ""}_${safe(contrato.titulo)}.pdf`,
  };
}
